import { WizardPanel } from '@/shared/components/Wizard';
import { Button, ValidationMessage } from '@schuettflix/react-components';
import { FormScheduleSchema, ScheduleSection } from '@/modules/location/components/schedule/ScheduleSection.tsx';
import { DriverActions, DriverActionSchema } from './components/DriverActions';
import { z } from 'zod';
import { useZodForm } from '@/shared/hooks/useZodForm';
import { Controller, FieldError, FieldErrorsImpl, useWatch } from 'react-hook-form';
import { FormLocationSelection, LocationSelection } from './components/LocationSelection';
import { Location, LocationType, OpeningTime } from './domain/LocationSchema';
import React, { useState } from 'react';
import { ContactPersonSection } from '@/modules/location/components/contactperson/ContactPersonSection.tsx';
import { ContactPersonSchema } from '@/modules/location/domain/ContactPersonSchema.ts';
import { LocationNotes } from './components/LocationNotes';
import { useTranslation } from 'react-i18next';
import { GeoAddressSection } from './components/geoaddress/GeoAddressSection';
import { v4 as uuidv4 } from 'uuid';
import { LocationSource, useLocations } from '@/clients/location/useLocations';
import { InlineLoadingSuspenseBoundary } from '@/shared/components/InlineLoadingSuspenseBoundary.tsx';
import { LocationAttachmentUploadSection } from '@/modules/location/components/LocationAttachmentUploadSection.tsx';
import { LocationAttachmentSchema } from '@/modules/location/domain/Attachment.ts';
import { TransferPoint } from '@/modules/location/components/transferpoint/TransferPoint.tsx';
import { Coordinates, CoordinatesSchema } from '@/modules/location/domain/GeoAddressSchema.ts';
import {
    LocationConfiguration,
    LocationConfigurationSchema,
} from '@/modules/location/domain/LocationConfigurationSchema.ts';
import { WithHook } from '@/shared/components/WithHook.tsx';
import { FormScheduleTime } from '@/modules/location/components/schedule/ScheduleTimePicker.tsx';
import { TextField } from '@schuettflix/planum-react';

const missingLocationKey = 'missing.location.key';
const FormLocationSchema = z.object({
    selection: FormLocationSelection.nullable().refine(selection => selection !== null, missingLocationKey),
    schedule: FormScheduleSchema,
    transferPoint: CoordinatesSchema.nullable(),
    note: z.string().nullable(),
});
type FormLocation = z.input<typeof FormLocationSchema>;

const LocationConfigurationFormDataSchema = z.object({
    location: FormLocationSchema.nullable(),
    driverActions: z.array(DriverActionSchema),
    contactPerson: ContactPersonSchema.nullable(),
    attachments: z.array(LocationAttachmentSchema),
});
type LocationConfigurationFormData = z.input<typeof LocationConfigurationFormDataSchema>;

const isLoadingLocation = (locationType: LocationType): boolean => {
    return locationType === 'LOADING';
};

const toOpeningTimes = (schedule: FormScheduleTime[]) => {
    return schedule.flatMap(entry => {
        return entry.weekdays.map(
            weekday =>
                ({
                    start: entry.start,
                    end: entry.end,
                    weekday: weekday,
                }) as OpeningTime
        );
    });
};

const toDomainData = (data: LocationConfigurationFormData) => {
    return {
        ...data,
        location: {
            ...data.location,
            ...data.location?.selection,
            openingTimes: toOpeningTimes(data.location?.schedule.filter(entry => entry.weekdays.length > 0) ?? []),
        },
    };
};

const toFormSchedule = (openingTimes: OpeningTime[]) => {
    const scheduleMap = new Map<string, FormScheduleTime>();

    openingTimes.forEach(openingTime => {
        const key = `${openingTime.start}-${openingTime.end}`;
        if (scheduleMap.has(key)) {
            scheduleMap.get(key)?.weekdays.push(openingTime.weekday);
        } else {
            scheduleMap.set(key, {
                start: openingTime.start,
                end: openingTime.end,
                weekdays: [openingTime.weekday],
            });
        }
    });

    return Array.from(scheduleMap.values());
};

const toFormData = (config: LocationConfiguration | null): LocationConfigurationFormData => {
    return {
        location: {
            selection: config?.location ?? null,
            schedule: config?.location.openingTimes ? toFormSchedule(config.location.openingTimes) : [],
            transferPoint: config?.location.transferPoint ?? null,
            note: config?.location.note ?? null,
        },
        contactPerson: config?.contactPerson ?? null,
        driverActions: config?.driverActions ?? [],
        attachments: config?.attachments ?? [],
    };
};

type LocationConfigurationPanelProps = {
    locationType: LocationType;
    value: LocationConfiguration | null;
    onSubmit: (config: LocationConfiguration | null) => void;
    onClose: () => void;
    source: Array<LocationSource>;
    defaultOrganizationId?: number;
};

export const LocationConfigurationPanel: React.FC<LocationConfigurationPanelProps> = ({
    locationType,
    value,
    onSubmit,
    onClose,
    source,
    defaultOrganizationId,
}) => {
    const defaultValues = toFormData(value);
    const methods = useZodForm({
        schema: LocationConfigurationFormDataSchema.transform(toDomainData).pipe(LocationConfigurationSchema),
        defaultValues: defaultValues,
    });
    const { handleSubmit } = methods;

    const { control } = methods;
    const { t } = useTranslation();
    const [customLocations, setCustomLocations] = useState<FormLocationSelection[]>([]);

    const fallbackScheduleTimes: FormScheduleTime[] = [
        {
            start: null,
            end: null,
            weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        },
    ];

    const selectableLocations = (locations: Location[]) => {
        if (value?.location.isCustom) {
            return [value.location].concat(locations);
        } else {
            return locations.map(location => (location.id === value?.location.id ? value.location : location));
        }
    };

    function asFormLocationError(error?: FieldError): FieldErrorsImpl<FormLocation> | undefined {
        function isFieldErrorsImplFormLocation(error: unknown): error is FieldErrorsImpl<FormLocation> {
            return typeof error === 'object' && error !== null;
        }

        return isFieldErrorsImplFormLocation(error) ? error : undefined;
    }

    const getSelectionErrorMessage = (error?: FieldErrorsImpl<FormLocation>) => {
        if (!error) return undefined;

        return error.selection?.message === missingLocationKey
            ? t('location.locationSelection.error')
            : error.selection?.message;
    };

    const [hasSubPanels, setHasSubPanels] = useState(false);

    const locationSelection = useWatch({ control, name: 'location.selection' });

    return (
        <WizardPanel
            noMaximize
            onClose={onClose}
            title={
                isLoadingLocation(locationType) ? t('location.panel.titleLoading') : t('location.panel.titleUnloading')
            }
            footer={({ hasSubPanels }) => (
                <LocationConfigurationFooter onSubmit={handleSubmit(onSubmit)} hasSubPanels={hasSubPanels} />
            )}
            onHasSubPanels={setHasSubPanels}
        >
            <InlineLoadingSuspenseBoundary>
                <WithHook hook={useLocations} hookArgs={[source]}>
                    {locations => (
                        <>
                            <Controller
                                control={control}
                                name="location"
                                render={({ field: { ref: _, ...field }, fieldState: { error: untypedError } }) => {
                                    const error = asFormLocationError(untypedError);
                                    const selection = field.value?.selection;

                                    return (
                                        <div className={'mb-4 flex flex-col gap-[40px]'}>
                                            <div className="flex flex-col gap-4">
                                                <LocationSelection
                                                    value={selection}
                                                    onChange={newSelection => {
                                                        const existingLocation = selectableLocations(locations).find(
                                                            location => location.id === newSelection?.id
                                                        );

                                                        const schedule =
                                                            existingLocation?.openingTimes &&
                                                            existingLocation.openingTimes.length > 0
                                                                ? toFormSchedule(existingLocation.openingTimes)
                                                                : fallbackScheduleTimes;

                                                        field.onChange({
                                                            selection: newSelection,
                                                            schedule: schedule,
                                                            transferPoint: existingLocation?.transferPoint ?? null,
                                                            note: existingLocation?.note ?? null,
                                                        });

                                                        const isMatchingLocation =
                                                            value?.location?.id === newSelection?.id;
                                                        methods.setValue(
                                                            'driverActions',
                                                            isMatchingLocation ? (value?.driverActions ?? []) : []
                                                        );
                                                        methods.setValue(
                                                            'contactPerson',
                                                            isMatchingLocation ? (value?.contactPerson ?? null) : null
                                                        );
                                                        methods.setValue(
                                                            'attachments',
                                                            isMatchingLocation ? (value?.attachments ?? []) : []
                                                        );
                                                    }}
                                                    errorMessage={getSelectionErrorMessage(error)}
                                                    locations={[...customLocations, ...selectableLocations(locations)]}
                                                    locationType={locationType}
                                                />
                                                <GeoAddressSection
                                                    onSubmit={(geoAddress): void => {
                                                        const newLocation: FormLocationSelection = {
                                                            id: uuidv4(),
                                                            geoAddress: geoAddress,
                                                            name: '',
                                                            isCustom: true,
                                                            organizationId: defaultOrganizationId,
                                                            legacyFactoryId: null,
                                                        };

                                                        setCustomLocations(prev => [newLocation, ...prev]);

                                                        field.onChange({
                                                            ...field.value,
                                                            schedule: fallbackScheduleTimes,
                                                            transferPoint: null,
                                                            note: null,
                                                            selection: newLocation,
                                                        });
                                                    }}
                                                    disabled={hasSubPanels}
                                                />
                                            </div>
                                            {selection && (
                                                <>
                                                    <TextField
                                                        value={selection.name}
                                                        className="-mb-4 -mt-[19px]"
                                                        label={t('location.panel.nameLabel')}
                                                        onChange={({ target: { value: locationName } }) =>
                                                            field.onChange({
                                                                ...field.value,
                                                                selection: {
                                                                    ...selection,
                                                                    name: locationName,
                                                                },
                                                            })
                                                        }
                                                    />
                                                    <TransferPoint
                                                        transferPoint={
                                                            field.value?.transferPoint ??
                                                            selection.geoAddress.coordinates
                                                        }
                                                        onChange={(newTransferPoint: Coordinates) => {
                                                            field.onChange({
                                                                ...field.value,
                                                                transferPoint: newTransferPoint,
                                                            });
                                                        }}
                                                        disabled={hasSubPanels}
                                                    />
                                                    <ScheduleSection
                                                        value={field.value?.schedule ?? []}
                                                        onChange={newSchedule => {
                                                            field.onChange({
                                                                ...field.value,
                                                                schedule: newSchedule,
                                                            });
                                                        }}
                                                        error={error?.schedule}
                                                    />
                                                    <LocationNotes
                                                        {...field}
                                                        value={field.value?.note ?? null}
                                                        onChange={newNote => {
                                                            field.onChange({
                                                                ...field.value,
                                                                note: newNote,
                                                            });
                                                        }}
                                                        locationType={locationType}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                            {locationSelection && (
                                <div className="flex flex-col gap-[40px]">
                                    <div className="flex flex-col gap-4">
                                        <Controller
                                            control={control}
                                            name="attachments"
                                            render={({ field: { ref: _, ...field } }) => {
                                                return <LocationAttachmentUploadSection {...field} />;
                                            }}
                                        />

                                        <Controller
                                            control={control}
                                            name="contactPerson"
                                            render={({ field: { ref: _, ...field } }) => (
                                                <ContactPersonSection {...field} disabled={hasSubPanels} />
                                            )}
                                        />
                                    </div>

                                    <Controller
                                        control={control}
                                        name="driverActions"
                                        render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                                            <ValidationMessage message={error?.message}>
                                                <DriverActions
                                                    isLoadingLocation={isLoadingLocation(locationType)}
                                                    value={field.value ?? []}
                                                    onChange={field.onChange}
                                                />
                                            </ValidationMessage>
                                        )}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </WithHook>
            </InlineLoadingSuspenseBoundary>
        </WizardPanel>
    );
};

interface LocationConfigurationFooterProps {
    onSubmit: () => void;
    hasSubPanels?: boolean;
}

export const LocationConfigurationFooter: React.FC<LocationConfigurationFooterProps> = ({ onSubmit, hasSubPanels }) => {
    const { t } = useTranslation();
    return (
        <div className="flex justify-end">
            <Button
                label={t('common.save')}
                size="sm"
                onClick={onSubmit}
                type="button"
                disabled={hasSubPanels}
                data-test="location-configuration-save-button"
            />
        </div>
    );
};
